import axios from "axios";
import { sessionExpiredDialog } from "../Utility/sessionExpired";

const axiosInstance = axios.create({
    baseURL: process.env.REACT_APP_BASE_URL,
    // Required so the browser automatically sends the HttpOnly biztantu_refresh cookie
    // on calls to /auth/refresh and /auth/logout.
    withCredentials: true,
});


// REQUEST INTERCEPTOR
// Attaches the current access token from localStorage to every outgoing request.
axiosInstance.interceptors.request.use(

    (config) => {

        const token = localStorage.getItem("accessToken");

        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        return config;
    },

    (error) => {
        return Promise.reject(error);
    }
);


// ─── Refresh-token queue ─────────────────────────────────────────────────────
// When multiple requests fail with 401 simultaneously, only ONE refresh call
// should go to the server. All other failed requests are held in this queue and
// resolved (or rejected) once the single refresh attempt finishes.

let isRefreshing = false;
let pendingRequests = [];

/**
 * Resolves all queued requests with the new access token,
 * or rejects them all if the refresh itself failed.
 */
const flushPendingRequests = (error, newToken) => {
    pendingRequests.forEach(({ resolve, reject }) => {
        if (error) {
            reject(error);
        } else {
            resolve(newToken);
        }
    });
    pendingRequests = [];
};
// ─────────────────────────────────────────────────────────────────────────────


// Endpoints that should never trigger a token refresh:
//  • "authenticate"  — Login API (bad credentials returns 401, not an expired session)
//  • "auth/refresh"   — Refresh API itself (prevents infinite loop if refresh token is dead)
//  • "offline"        — Telecalling presence cleanup called on logout
const REFRESH_EXCLUDED_ENDPOINTS = [
    "authenticate",
    "auth/refresh",
    "offline",
];

const isExcludedFromRefresh = (url = "") =>
    REFRESH_EXCLUDED_ENDPOINTS.some((endpoint) => url.includes(endpoint));


// RESPONSE INTERCEPTOR
axiosInstance.interceptors.response.use(

    (response) => {
        return response;
    },

    async (error) => {

        const originalRequest = error.config;

        // Early exit: only proceed with token refresh if:
        //  1. The error status is 401 Unauthorized
        //  2. The request has not already been retried once
        //  3. The URL is not in our excluded endpoints list
        if (
            error.response?.status !== 401 ||
            originalRequest?._retry ||
            isExcludedFromRefresh(originalRequest?.url)
        ) {
            return Promise.reject(error);
        }

        // If a refresh is already in flight, queue this request and wait.
        if (isRefreshing) {
            return new Promise((resolve, reject) => {
                pendingRequests.push({ resolve, reject });
            }).then((newToken) => {
                originalRequest.headers.Authorization = `Bearer ${newToken}`;
                return axiosInstance(originalRequest);
            }).catch((err) => {
                return Promise.reject(err);
            });
        }

        // This is the first 401 — start the refresh flow.
        originalRequest._retry = true;
        isRefreshing = true;

        try {

            // Browser automatically sends the HttpOnly biztantu_refresh cookie.
            // Backend validates the session, issues a new access token.
            const refreshResponse = await axiosInstance.post(
                "auth/refresh",
                {},
                { withCredentials: true }
            );

            const newAccessToken = refreshResponse.data.token;

            // Persist the new access token for subsequent requests.
            localStorage.setItem("accessToken", newAccessToken);

            // Unblock all queued requests with the new token.
            flushPendingRequests(null, newAccessToken);
            isRefreshing = false;

            // Retry the original request that triggered the 401.
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
            originalRequest.withCredentials = true;
            return axiosInstance(originalRequest);

        } catch (refreshError) {

            // Refresh itself failed (session revoked / refresh token expired).
            // Unblock all queued requests with the error so they reject cleanly.
            flushPendingRequests(refreshError, null);
            isRefreshing = false;

            // Show the session expired dialog and clear local state.
            // requestLogout() (called inside sessionExpiredDialog) will also
            // fire POST /auth/logout to revoke the session server-side.
            sessionExpiredDialog();

            return Promise.reject(refreshError);
        }
    }
);

export default axiosInstance;